import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Snackbar,
  Alert,
  alpha,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import FilterListIcon from "@mui/icons-material/FilterList";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { isMSP } from "../../helpers/util_helper";
import { getInvoiceDownload, getInvoicePreview, generateInvoice, postInvoiceAction, getCustomerPurchaseOrders } from "../../helpers/auth_aws_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import {
  getCustomerInvoices,
  getCustomerBillingMetrics,
  getInvoiceRuleSnapshots,
} from "../../store/billingManagement/billingManagementSlice";
import CustomerInvoicesHeader from "./CustomerInvoicesHeader";
import CustomerInvoicesMetrics, { formatCurrency } from "./CustomerInvoicesMetrics";
import { useBillingCustomerOrgDisplay } from "./useBillingCustomerOrgDisplay";
import {
  AWS_PARTNER_SUPPORT_RULE_TYPE,
  formatAwsPartnerSupportParameterDisplay,
} from "./awsPartnerSupportBilling";
import {
  APPLIES_TO_RULE_TYPES_KEY,
  getSelectedAppliesToRuleTypeValues,
  formatAppliesToRuleTypeLabel,
} from "./billingTaxRuleParameters";

// API sends datetimes in UTC; parse as UTC (append Z if no timezone) so toLocale* shows browser local time
const parseUtc = (dateString) => {
  const s = String(dateString).trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  const isIsoDatetime = /^\d{4}-\d{2}-\d{2}T\d/.test(s);
  const hasTimezone = s.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(s);
  const asUtc = isIsoDatetime && !hasTimezone ? `${s.replace(/\.\d+$/, "")}Z` : s;
  const date = new Date(asUtc);
  return isNaN(date.getTime()) ? null : date;
};

const formatDate = (dateString) => {
  const date = parseUtc(dateString);
  if (!date) return "—";
  try {
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

const formatDateTime = (dateString) => {
  const date = parseUtc(dateString);
  if (!date) return "—";
  try {
    return date.toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
};

// Sum amount from metrics.by_rule_category for an invoice
const getInvoiceAmount = (inv) => {
  const m = inv.metrics?.by_rule_category;
  if (!m || typeof m !== "object") return null;
  const sum = Object.values(m).reduce((a, b) => a + (Number(b) || 0), 0);
  return sum;
};

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// Year options for filter: current year down to 2025
const YEAR_OPTIONS = Array.from({ length: Math.max(0, new Date().getFullYear() + 1 - 2025) }, (_, i) => 2025 + i).reverse();

const COLUMN_STORAGE_KEY = "customerInvoices_columnVisibility";
const STATUS_FILTER_STORAGE_KEY = "customerInvoices_statusFilter";

const STATUS_FILTER_MODES = [
  { value: "all", label: "All" },
  { value: "active", label: "Active/Failed" },
  { value: "active_only", label: "Active" },
  { value: "sent", label: "Sent" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];

const loadStatusFilter = () => {
  try {
    const raw = localStorage.getItem(STATUS_FILTER_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed === "string" && ["all", "active", "active_only", "sent", "cancelled", "failed"].includes(parsed)) return parsed;
      if (Array.isArray(parsed)) return "active";
    }
  } catch (_) {}
  return "active";
};

const saveStatusFilter = (mode) => {
  try {
    localStorage.setItem(STATUS_FILTER_STORAGE_KEY, JSON.stringify(mode));
  } catch (_) {}
};

const RUN_TYPE_FILTER_STORAGE_KEY = "customerInvoices_runTypeFilter";
const RUN_TYPE_FILTER_MODES = [
  { value: "all", label: "All" },
  { value: "on_demand", label: "On demand" },
  { value: "recurring", label: "Recurring" },
];

const loadRunTypeFilter = () => {
  try {
    const raw = localStorage.getItem(RUN_TYPE_FILTER_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed === "string" && ["all", "on_demand", "recurring"].includes(parsed)) return parsed;
    }
  } catch (_) {}
  return "all";
};

const saveRunTypeFilter = (mode) => {
  try {
    localStorage.setItem(RUN_TYPE_FILTER_STORAGE_KEY, JSON.stringify(mode));
  } catch (_) {}
};

// Column definitions: id, label, defaultVisible (combined columns: requested, approved, sent, cancelled show by/at + reason for cancelled)
const TABLE_COLUMNS = [
  { id: "billing_period", label: "Billing period", defaultVisible: true },
  { id: "amount", label: "Amount", defaultVisible: true },
  { id: "status", label: "Status", defaultVisible: true },
  { id: "run_type", label: "Run Type", defaultVisible: true },
  { id: "requested", label: "Requested", defaultVisible: true },
  { id: "finalized_at", label: "Finalized at", defaultVisible: true },
  { id: "approved", label: "Approved", defaultVisible: false },
  { id: "sent", label: "Sent", defaultVisible: false },
  { id: "cancelled", label: "Cancelled", defaultVisible: false },
  { id: "applied_rules", label: "Applied Rules", defaultVisible: true },
  { id: "actions", label: "Actions", defaultVisible: true },
];

const getDefaultColumnVisibility = () =>
  TABLE_COLUMNS.reduce((acc, col) => ({ ...acc, [col.id]: col.defaultVisible }), {});

const loadColumnVisibility = () => {
  try {
    const raw = localStorage.getItem(COLUMN_STORAGE_KEY);
    const defaults = getDefaultColumnVisibility();
    if (raw) {
      const parsed = JSON.parse(raw);
      // Migrate old column keys to combined columns
      const migrated = {};
      TABLE_COLUMNS.forEach((col) => {
        if (parsed[col.id] !== undefined) {
          migrated[col.id] = !!parsed[col.id];
        } else if (col.id === "requested" && (parsed.requested_at !== undefined || parsed.requested_by !== undefined)) {
          migrated.requested = !!(parsed.requested_at || parsed.requested_by);
        } else if (col.id === "approved" && (parsed.approved_at !== undefined || parsed.approved_by !== undefined)) {
          migrated.approved = !!(parsed.approved_at || parsed.approved_by);
        } else if (col.id === "sent" && (parsed.sent_at !== undefined || parsed.sent_by !== undefined)) {
          migrated.sent = !!(parsed.sent_at || parsed.sent_by);
        } else if (col.id === "cancelled" && (parsed.cancelled_at !== undefined || parsed.cancelled_by !== undefined || parsed.cancel_reason !== undefined)) {
          migrated.cancelled = !!(parsed.cancelled_at || parsed.cancelled_by || parsed.cancel_reason);
        }
      });
      return { ...defaults, ...migrated };
    }
  } catch (_) {}
  return getDefaultColumnVisibility();
};

const saveColumnVisibility = (visibility) => {
  try {
    localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(visibility));
  } catch (_) {}
};

// Format billing period as "Month YYYY" (e.g. November 2025) from date string
const formatMonthYear = (dateString) => {
  if (!dateString) return "—";
  try {
    const s = String(dateString);
    const match = s.match(/^(\d{4})-(\d{2})/);
    if (match) {
      const [, y, m] = match;
      const monthNum = parseInt(m, 10);
      return `${MONTH_NAMES[monthNum - 1]} ${y}`;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
  } catch {
    return "—";
  }
};

const CustomerInvoices = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { customerOrgCode } = useParams();

  const isMSPUser = isMSP();

  const { displayName: resolvedCustomerName } = useBillingCustomerOrgDisplay(customerOrgCode, {
    enabled: isMSPUser,
    fallbackName: location.state?.customerName || "",
  });
  const customerName = resolvedCustomerName || "Customer";
  const {
    invoices,
    isFetchingInvoices,
    fetchInvoicesError,
    invoicesPagination,
    customerMetrics,
    isFetchingCustomerMetrics,
    customerMetricsError,
    invoiceRuleSnapshots,
    isFetchingInvoiceRuleSnapshots,
    fetchInvoiceRuleSnapshotsError,
  } = useSelector((state) => state.billingManagement);

  const [yearFilter, setYearFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [previewingInvoiceId, setPreviewingInvoiceId] = useState(null);
  const [downloadSnackbar, setDownloadSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [rulesModalOpen, setRulesModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState(loadColumnVisibility);
  const [columnsAnchorEl, setColumnsAnchorEl] = useState(null);
  const columnsMenuOpen = Boolean(columnsAnchorEl);
  const [statusFilterMode, setStatusFilterMode] = useState(() => loadStatusFilter());
  const [statusFilterAnchorEl, setStatusFilterAnchorEl] = useState(null);
  const statusFilterMenuOpen = Boolean(statusFilterAnchorEl);
  const [runTypeFilterMode, setRunTypeFilterMode] = useState(() => loadRunTypeFilter());
  const [runTypeFilterAnchorEl, setRunTypeFilterAnchorEl] = useState(null);
  const runTypeFilterMenuOpen = Boolean(runTypeFilterAnchorEl);
  const [pageSize, setPageSize] = useState(15);
  // Generate invoice dialog (period from row; confirmation only)
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generatePeriod, setGeneratePeriod] = useState(null); // { year, month, label }
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [generateNoRules, setGenerateNoRules] = useState(false);
  const [generateNoRulesMessage, setGenerateNoRulesMessage] = useState("");
  const [generatePoList, setGeneratePoList] = useState([]);
  const [generatePoId, setGeneratePoId] = useState("");
  const [generatePoLoading, setGeneratePoLoading] = useState(false);
  // Invoice action dialog (opened from Status column button)
  const [actionInvoice, setActionInvoice] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Download invoice: get pre-signed URL and open in new tab
  const handleDownloadInvoice = async (invoiceId) => {
    if (!customerOrgCode || !invoiceId) return;
    setDownloadingInvoiceId(invoiceId);
    setDownloadSnackbar((prev) => ({ ...prev, open: false }));
    try {
      const response = await getInvoiceDownload(customerOrgCode, invoiceId);
      if (response?.status === 200 && response?.url) {
        window.open(response.url, "_blank", "noopener,noreferrer");
        setDownloadSnackbar({ open: true, message: "Download started.", severity: "success" });
      } else {
        const data = response?.data ?? response;
        const msg = data != null ? getApiErrorMessage(data) : (response?.message || "Download not available for this invoice.");
        setDownloadSnackbar({ open: true, message: String(msg), severity: "error" });
      }
    } catch (error) {
      const data = error?.response?.data ?? error?.data;
      const message = data != null ? getApiErrorMessage(data) : (error?.message || "Download failed.");
      setDownloadSnackbar({ open: true, message: String(message), severity: "error" });
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  // Preview invoice: get pre-signed URL and open in new tab
  const handlePreviewInvoice = async (invoiceId) => {
    if (!customerOrgCode || !invoiceId) return;
    setPreviewingInvoiceId(invoiceId);
    setDownloadSnackbar((prev) => ({ ...prev, open: false }));
    try {
      const response = await getInvoicePreview(customerOrgCode, invoiceId);
      if (response?.status === 200 && response?.url) {
        window.open(response.url, "_blank", "noopener,noreferrer");
        setDownloadSnackbar({ open: true, message: "Preview opened in new tab.", severity: "success" });
      } else {
        const data = response?.data ?? response;
        const msg = data != null ? getApiErrorMessage(data) : (response?.message || "Preview not available for this invoice.");
        setDownloadSnackbar({ open: true, message: String(msg), severity: "error" });
      }
    } catch (error) {
      const data = error?.response?.data ?? error?.data;
      const message = data != null ? getApiErrorMessage(data) : (error?.message || "Preview failed.");
      setDownloadSnackbar({ open: true, message: String(message), severity: "error" });
    } finally {
      setPreviewingInvoiceId(null);
    }
  };

  const handleOpenRulesModal = (invoiceId) => {
    if (!customerOrgCode || !invoiceId) return;
    setSelectedInvoiceId(invoiceId);
    setRulesModalOpen(true);
    dispatch(getInvoiceRuleSnapshots({ customerOrgCode, invoiceId }));
  };

  const handleCloseRulesModal = () => {
    setRulesModalOpen(false);
    setSelectedInvoiceId(null);
  };

  // Parse billing period from invoice row (e.g. "2026-02-01" -> { year: 2026, month: 2 })
  const getPeriodFromInvoice = (inv) => {
    const start = inv?.billing_period_start || inv?.billing_period_end;
    if (!start || typeof start !== "string") return null;
    const match = String(start).match(/^(\d{4})-(\d{2})/);
    if (!match) return null;
    const [, y, m] = match;
    const year = parseInt(y, 10);
    const month = parseInt(m, 10);
    if (month < 1 || month > 12) return null;
    const label = formatMonthYear(start);
    return { year, month, label };
  };

  const appliedRulesModalTitle = useMemo(() => {
    if (!selectedInvoiceId || !Array.isArray(invoices)) return "Applied Rules";
    const inv = invoices.find((i) => i.invoice_id === selectedInvoiceId);
    const period = inv ? getPeriodFromInvoice(inv) : null;
    if (!period) return "Applied Rules";
    return `Applied Rules - ${MONTH_NAMES[period.month - 1]} - ${period.year}`;
  }, [selectedInvoiceId, invoices]);

  const handleOpenGenerateDialog = (inv) => {
    const period = getPeriodFromInvoice(inv);
    if (!period) return;
    setGeneratePoLoading(true);
    setGeneratePoList([]);
    setGeneratePoId("");
    setGeneratePeriod(period);
    setGenerateNoRules(false);
    setGenerateNoRulesMessage("");
    setGenerateError(null);
    setGenerateDialogOpen(true);
  };

  const handleCloseGenerateDialog = () => {
    setGenerateDialogOpen(false);
    setGenerateNoRules(false);
    setGenerateError(null);
    setGenerateLoading(false);
    // Keep period + PO list/loading until next open so closing does not flash empty PO UI.
  };

  useEffect(() => {
    if (!generateDialogOpen || !customerOrgCode) return;
    setGeneratePoLoading(true);
    getCustomerPurchaseOrders(customerOrgCode)
      .then((res) => {
        const data = res?.data ?? res;
        const list = Array.isArray(data?.items) ? data.items : [];
        setGeneratePoList(list);
        setGeneratePoId((prev) => {
          if (list.length === 0) return "";
          return prev && list.some((p) => p.id === prev) ? prev : list[0].id;
        });
      })
      .catch(() => {
        setGeneratePoList([]);
        setGeneratePoId("");
      })
      .finally(() => setGeneratePoLoading(false));
  }, [generateDialogOpen, customerOrgCode]);

  const handleGoToPurchaseOrdersFromGenerate = () => {
    if (!customerOrgCode) return;
    handleCloseGenerateDialog();
    history.push(`/billing-management/customer/${customerOrgCode}/purchase-orders`, {
      customerName: customerName && customerName !== "Customer" ? customerName : undefined,
      openCreateModal: true,
      returnTo: `/billing-management/customer/${customerOrgCode}/invoices`,
    });
  };

  const handleGenerateInvoice = async (overrideWarnings = false) => {
    if (!customerOrgCode || !generatePeriod) return;
    const { year, month } = generatePeriod;
    setGenerateLoading(true);
    setGenerateError(null);
    try {
      const payload = { year, month, override_warnings: overrideWarnings };
      if (generatePoId && generatePoId.trim()) payload.po_id = generatePoId.trim();
      const response = await generateInvoice(customerOrgCode, payload);
      const data = response?.data ?? response;
      const message = data?.message ?? response?.message ?? "";
      const status = response?.status;
      if (status === 200 || (data && (status == null || status === 200))) {
        const msgLower = String(message).toLowerCase();
        if (!overrideWarnings && (msgLower.includes("no rules") || msgLower.includes("no rules found"))) {
          setGenerateNoRules(true);
          setGenerateNoRulesMessage(message || "No rules found for the selected date range.");
        } else {
          setDownloadSnackbar({ open: true, message: message || "Invoice generation request accepted.", severity: "success" });
          handleCloseGenerateDialog();
          dispatch(getCustomerInvoices({ customerOrgCode, page, page_size: pageSize, ...(yearFilter ? { year: yearFilter } : {}) }));
          if (customerOrgCode) {
            dispatch(getCustomerBillingMetrics({ customerOrgCode, ...(yearFilter ? { year: yearFilter } : {}) }));
          }
        }
      } else {
        setGenerateError(getApiErrorMessage(data));
      }
    } catch (error) {
      const errData = error?.response?.data ?? error?.data;
      setGenerateError(getApiErrorMessage(errData) || error?.message || "Request failed.");
    } finally {
      setGenerateLoading(false);
    }
  };

  const INVOICE_ACTIONS = [
    { id: "APPROVE", label: "Approve" },
    { id: "UNAPPROVE", label: "Unapprove" },
    { id: "SEND", label: "Send" },
    { id: "RESEND", label: "Resend" },
    { id: "RETRY", label: "Retry delivery" },
    { id: "CANCEL", label: "Cancel invoice" },
  ];

  // Preconditions: which action(s) to show as buttons in Status column (APPROVED can show both Send and Unapprove)
  const getActionsForInvoice = (inv) => {
    const status = (inv?.status || "").toString().toUpperCase();
    const ui = (inv?.ui_status || inv?.status || "").toUpperCase();
    if (status === "FINALIZED" && ui === "PENDING_APPROVAL") return [{ action: "CANCEL", label: "Cancel" }, { action: "APPROVE", label: "Approve" }];
    if (ui === "PENDING_APPROVAL") return [{ action: "APPROVE", label: "Approve" }];
    if (ui === "APPROVED") return [{ action: "SEND", label: "Send" }, { action: "UNAPPROVE", label: "Unapprove" }];
    if (ui === "DELIVERY_FAILED") return [{ action: "RETRY", label: "Retry" }];
    if (ui === "SENT") return [{ action: "RESEND", label: "Resend" }, { action: "CANCEL", label: "Cancel" }];
    if (status === "FAILED") return [{ action: "CANCEL", label: "Cancel" }];
    return [];
  };

  // Preview allowed for Finalized, Sent, or Cancelled (PDF is available for these states)
  const canPreviewInvoice = (inv) => {
    if (!inv?.invoice_id) return false;
    const status = (inv?.status || "").toString().toUpperCase();
    return status === "FINALIZED" || status === "SENT" || status === "CANCELLED";
  };

  const handleOpenActionDialog = (inv, actionType) => {
    setActionInvoice(inv);
    setActionType(actionType);
    setCancelReason("");
    setActionError(null);
    setActionDialogOpen(true);
  };

  const handleCloseActionDialog = () => {
    setActionDialogOpen(false);
    setActionType(null);
    setActionInvoice(null);
    setCancelReason("");
    setActionError(null);
    setActionLoading(false);
  };

  const handleConfirmInvoiceAction = async () => {
    if (!customerOrgCode || !actionInvoice?.invoice_id || !actionType) return;
    const status = (actionInvoice?.status || "").toString().toUpperCase();
    const uiStatus = (actionInvoice?.ui_status || "").toString().toUpperCase();
    const cancelNoReason = actionType === "CANCEL" && ((status === "FINALIZED" && uiStatus === "PENDING_APPROVAL") || status === "FAILED");
    const payload = actionType === "CANCEL" ? { action: "CANCEL", cancel_reason: cancelNoReason ? "" : (cancelReason || null) } : { action: actionType, cancel_reason: null };
    setActionLoading(true);
    setActionError(null);
    try {
      const response = await postInvoiceAction(customerOrgCode, actionInvoice.invoice_id, payload);
      const data = response?.data ?? response;
      const message = data?.message ?? response?.message ?? "";
      const status = response?.status;
      if (status === 200 || (data && (status == null || status === 200))) {
        setDownloadSnackbar({ open: true, message: message || "Action completed.", severity: "success" });
        handleCloseActionDialog();
        dispatch(getCustomerInvoices({ customerOrgCode, page, page_size: pageSize, ...(yearFilter ? { year: yearFilter } : {}) }));
        if (customerOrgCode) dispatch(getCustomerBillingMetrics({ customerOrgCode, ...(yearFilter ? { year: yearFilter } : {}) }));
      } else {
        setActionError(getApiErrorMessage(data) || message || "Action failed.");
      }
    } catch (error) {
      const errData = error?.response?.data ?? error?.data;
      setActionError(errData != null ? getApiErrorMessage(errData) : (error?.message || "Action failed."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleColumnVisibilityChange = (columnId) => (e) => {
    const next = { ...columnVisibility, [columnId]: e.target.checked };
    setColumnVisibility(next);
    saveColumnVisibility(next);
  };

  const handleStatusFilterModeChange = (mode) => {
    setStatusFilterMode(mode);
    saveStatusFilter(mode);
    setStatusFilterAnchorEl(null);
  };

  const handleRunTypeFilterModeChange = (mode) => {
    setRunTypeFilterMode(mode);
    saveRunTypeFilter(mode);
    setRunTypeFilterAnchorEl(null);
  };

  const visibleColumns = TABLE_COLUMNS.filter((col) => columnVisibility[col.id]);
  const colSpanForEmpty = visibleColumns.length;

  const stickyHeaderCell = (colId, isFirst, isLast) => {
    const base = { backgroundColor: "#f4f4f5", borderBottom: "2px solid #e4e4e7", py: 1.5, px: 2, fontWeight: 600, fontSize: "0.875rem", color: "#52525b" };
    if (isFirst) return { ...base, position: "sticky", left: 0, zIndex: 2, minWidth: 140, boxShadow: "2px 0 4px rgba(0,0,0,0.06)" };
    if (isLast) return { ...base, position: "sticky", right: 0, zIndex: 2, minWidth: 160, boxShadow: "-2px 0 4px rgba(0,0,0,0.06)" };
    return base;
  };

  const stickyBodyCell = (colId, isFirst, isLast) => {
    const base = { borderBottom: "1px solid rgba(0,0,0,0.06)", py: 1.5, px: 2, fontSize: "0.875rem", backgroundColor: "#fff" };
    if (isFirst) return { ...base, position: "sticky", left: 0, zIndex: 1, minWidth: 140, boxShadow: "2px 0 4px rgba(0,0,0,0.06)" };
    if (isLast) return { ...base, position: "sticky", right: 0, zIndex: 1, minWidth: 160, boxShadow: "-2px 0 4px rgba(0,0,0,0.06)" };
    return base;
  };

  useEffect(() => {
    if (!isMSPUser) {
      history.push("/dashboard");
      return;
    }
  }, [isMSPUser, history]);

  useEffect(() => {
    if (customerOrgCode && isMSPUser) {
      const params = { page, page_size: pageSize };
      if (yearFilter) params.year = yearFilter;
      dispatch(getCustomerInvoices({ customerOrgCode, ...params }));
    }
  }, [customerOrgCode, isMSPUser, dispatch, page, pageSize, yearFilter]);

  // Fetch customer-scope billing metrics (API: scope=customer, customer_orgcode required)
  useEffect(() => {
    if (customerOrgCode && isMSPUser) {
      const payload = { customerOrgCode };
      if (yearFilter) payload.year = yearFilter;
      dispatch(getCustomerBillingMetrics(payload));
    }
  }, [customerOrgCode, isMSPUser, dispatch, yearFilter]);

  const handleRefreshGrid = () => {
    if (!customerOrgCode) return;
    const params = { page, page_size: pageSize };
    if (yearFilter) params.year = yearFilter;
    dispatch(getCustomerInvoices({ customerOrgCode, ...params }));
    dispatch(getCustomerBillingMetrics({ customerOrgCode, ...(yearFilter ? { year: yearFilter } : {}) }));
  };

  const filteredInvoices = (invoices || []).filter((inv) => {
    const status = (inv.ui_status || inv.status || "—").toString().toUpperCase();
    let passesStatus = true;
    if (statusFilterMode === "all") passesStatus = true;
    else if (statusFilterMode === "active") passesStatus = status !== "CANCELLED";
    else if (statusFilterMode === "active_only") passesStatus = status !== "CANCELLED" && status !== "FAILED";
    else if (statusFilterMode === "sent") passesStatus = status === "SENT";
    else if (statusFilterMode === "cancelled") passesStatus = status === "CANCELLED";
    else if (statusFilterMode === "failed") passesStatus = status === "FAILED";
    if (!passesStatus) return false;
    const runType = (inv.generation_type || "").toString().toUpperCase();
    let passesRunType = true;
    if (runTypeFilterMode === "all") passesRunType = true;
    else if (runTypeFilterMode === "on_demand") passesRunType = runType === "ON_DEMAND";
    else if (runTypeFilterMode === "recurring") passesRunType = runType === "RECURRING" || runType === "SCHEDULED";
    if (!passesRunType) return false;
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (inv.invoice_id && inv.invoice_id.toLowerCase().includes(term)) ||
      (inv.billing_period_start && inv.billing_period_start.includes(term)) ||
      (inv.billing_period_end && inv.billing_period_end.includes(term))
    );
  });

  // Metric cards use API customer-scope metrics when available; fallback to list-derived values only for table context
  const totalInvoices = customerMetrics?.total_invoices ?? invoicesPagination?.total ?? 0;
  const totalBilled = customerMetrics?.total_billed ?? (invoices || []).reduce((sum, inv) => sum + (getInvoiceAmount(inv) || 0), 0);
  const latestInvoiceAmount = customerMetrics?.latest_invoice_amount ?? (invoices?.[0] ? getInvoiceAmount(invoices[0]) : null);

  if (!customerOrgCode) {
    history.replace("/billing-management");
    return null;
  }

  return (
    <Box
      sx={{
        padding: { xs: "1rem", sm: "1.5rem", md: "2rem" },
        mb: "1.5rem",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
        fontFamily: '"Inter", system-ui, sans-serif',
      }}
    >
      <CustomerInvoicesHeader customerName={customerName} />

      <CustomerInvoicesMetrics
        customerName={customerName}
        totalInvoices={totalInvoices}
        totalBilled={totalBilled}
        latestInvoiceAmount={latestInvoiceAmount}
        lastInvoicePeriodStart={invoices?.[0]?.billing_period_start}
        customerMetrics={customerMetrics}
        isFetchingCustomerMetrics={isFetchingCustomerMetrics}
        customerMetricsError={customerMetricsError}
      />

      {/* Table card: filters in header + table */}
      <Card sx={{ borderRadius: "8px", border: "1px solid #e5e7eb", boxShadow: "none", backgroundColor: "#ffffff", overflow: "hidden" }}>
        {/* Table header: Year dropdown + Search */}
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2, p: 2, borderBottom: "1px solid #e5e7eb", backgroundColor: "#fafafa" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography component="span" sx={{ color: "#64748b", fontSize: "0.875rem", mr: 1.5 }}>
              Month view
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={yearFilter || ""}
                onChange={(e) => setYearFilter(e.target.value ? Number(e.target.value) : "")}
                displayEmpty
                renderValue={(v) => (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <CalendarMonthIcon sx={{ color: "#64748b", fontSize: 20 }} />
                    <span>{v ? `Year: ${v}` : "All years"}</span>
                  </Box>
                )}
                sx={{
                  height: 40,
                  backgroundColor: "#ffffff",
                  borderRadius: 1,
                  "& .MuiSelect-select": { py: 1.25, pl: 1.5 },
                }}
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 320, mt: 1.5, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" } },
                }}
              >
                <MenuItem value="">
                  <em>All years</em>
                </MenuItem>
                {YEAR_OPTIONS.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <TextField
              size="small"
              placeholder="Search by month or invoice ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 260, "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
            />
            <Tooltip title="Refresh list">
              <span>
                <IconButton
                  size="small"
                  onClick={handleRefreshGrid}
                  disabled={isFetchingInvoices}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: "#ffffff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 1,
                    color: "#52525b",
                    "&:hover": { borderColor: "#94a3b8", backgroundColor: "#fafafa", color: "#18181b" },
                    "&.Mui-disabled": { backgroundColor: "#fafafa", borderColor: "#e5e7eb" },
                  }}
                >
                  {isFetchingInvoices ? (
                    <CircularProgress size={20} sx={{ color: "#64748b" }} />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ViewColumnIcon />}
              onClick={(e) => setColumnsAnchorEl(e.currentTarget)}
              sx={{
                height: 40,
                backgroundColor: "#ffffff",
                borderRadius: 1,
                borderColor: "#e5e7eb",
                color: "#52525b",
                textTransform: "none",
                "&:hover": { borderColor: "#94a3b8", backgroundColor: "#fafafa" },
              }}
            >
              Columns
            </Button>
          </Box>
          <Popover
            open={columnsMenuOpen}
            anchorEl={columnsAnchorEl}
            onClose={() => setColumnsAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { mt: 1.5, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", minWidth: 220, p: 1.5 } }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#52525b", mb: 1.5, px: 0.5 }}>
              Show / hide columns
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  const next = TABLE_COLUMNS.reduce((acc, col) => ({ ...acc, [col.id]: true }), {});
                  setColumnVisibility(next);
                  saveColumnVisibility(next);
                }}
                sx={{ textTransform: "none", fontSize: "0.8125rem", color: "#3b82f6", minWidth: 0, px: 1 }}
              >
                Show all
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  const next = TABLE_COLUMNS.reduce((acc, col) => ({ ...acc, [col.id]: false }), {});
                  setColumnVisibility(next);
                  saveColumnVisibility(next);
                }}
                sx={{ textTransform: "none", fontSize: "0.8125rem", color: "#64748b", minWidth: 0, px: 1 }}
              >
                Hide all
              </Button>
            </Box>
            <FormGroup>
              {TABLE_COLUMNS.map((col) => (
                <FormControlLabel
                  key={col.id}
                  control={
                    <Checkbox
                      checked={!!columnVisibility[col.id]}
                      onChange={handleColumnVisibilityChange(col.id)}
                      size="small"
                      sx={{ color: "#71717a", "&.Mui-checked": { color: "#3b82f6" } }}
                    />
                  }
                  label={col.label}
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.875rem", color: "#374151" } }}
                />
              ))}
            </FormGroup>
          </Popover>
        </Box>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="medium" stickyHeader sx={{ minWidth: 720, fontSize: "0.875rem" }}>
            <TableHead>
              <TableRow>
                {visibleColumns.map((col, index) => (
                  <TableCell
                    key={col.id}
                    align={["actions", "applied_rules", "status", "run_type"].includes(col.id) ? "center" : "left"}
                    sx={stickyHeaderCell(col.id, index === 0, index === visibleColumns.length - 1)}
                  >
                    {col.id === "status" ? (
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        <span>{col.label}</span>
                        <Tooltip title="Filter by status">
                          <IconButton
                            size="small"
                            onClick={(e) => setStatusFilterAnchorEl(e.currentTarget)}
                            sx={{ p: 0.25, color: statusFilterMode !== "active" ? "primary.main" : "inherit" }}
                            aria-label="Filter by status"
                          >
                            <FilterListIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : col.id === "run_type" ? (
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        <span>{col.label}</span>
                        <Tooltip title="Filter by run type">
                          <IconButton
                            size="small"
                            onClick={(e) => setRunTypeFilterAnchorEl(e.currentTarget)}
                            sx={{ p: 0.25, color: runTypeFilterMode !== "all" ? "primary.main" : "inherit" }}
                            aria-label="Filter by run type"
                          >
                            <FilterListIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetchingInvoices ? (
                <TableRow>
                  <TableCell colSpan={colSpanForEmpty} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} sx={{ color: "#3b82f6" }} />
                  </TableCell>
                </TableRow>
              ) : fetchInvoicesError ? (
                <TableRow>
                  <TableCell colSpan={colSpanForEmpty} align="center" sx={{ py: 4, color: "#ef4444", fontSize: "0.875rem" }}>
                    {typeof fetchInvoicesError === "string"
                      ? fetchInvoicesError
                      : (getApiErrorMessage(fetchInvoicesError) || "Failed to load invoices.")}
                  </TableCell>
                </TableRow>
              ) : filteredInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colSpanForEmpty} align="center" sx={{ py: 6, color: "#71717a", fontSize: "0.875rem" }}>
                    No invoices found
                  </TableCell>
                </TableRow>
              ) : (
                filteredInvoices.map((inv) => {
                  const monthLabel = formatMonthYear(inv.billing_period_start || inv.billing_period_end);
                  const uiStatus = inv.ui_status || inv.status || "—";
                  const renderCell = (colId, colIndex) => {
                    const sticky = stickyBodyCell(colId, colIndex === 0, colIndex === visibleColumns.length - 1);
                    switch (colId) {
                      case "billing_period":
                        return <TableCell key={colId} sx={{ ...sticky, color: "#52525b" }}>{monthLabel}</TableCell>;
                      case "amount":
                        return (
                          <TableCell key={colId} sx={sticky} align="left">
                            <Box>
                              <Typography sx={{ fontWeight: 600, color: "#0f172a", fontSize: "0.875rem" }}>
                                {formatCurrency(inv.total_calculated_amount)}
                              </Typography>
                              {inv.invoice_display && (
                                <Typography sx={{ display: "block", fontSize: "0.75rem", color: "#94a3b8", fontFamily: "monospace", mt: 0.25 }}>
                                  INV-{inv.invoice_display}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        );
                      case "status": {
                        const actions = getActionsForInvoice(inv);
                        return (
                          <TableCell key={colId} align="center" sx={sticky}>
                            {actions.length > 0 ? (
                              <Tooltip title={`Status: ${uiStatus}`}>
                                <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 0.5 }}>
                                  {actions.map(({ action: act, label }) => (
                                    <Button
                                      key={act}
                                      size="small"
                                      variant={act === "UNAPPROVE" ? "outlined" : "contained"}
                                      disabled={actionLoading || !inv.invoice_id}
                                      onClick={() => handleOpenActionDialog(inv, act)}
                                      sx={{
                                        textTransform: "none",
                                        fontSize: "0.8125rem",
                                        fontWeight: 600,
                                        borderRadius: "8px",
                                        boxShadow: "none",
                                        ...(act === "CANCEL"
                                          ? {
                                              backgroundColor: "#64748b",
                                              color: "#fff",
                                              "&:hover": { backgroundColor: "#475569", boxShadow: "none" },
                                            }
                                          : act === "UNAPPROVE"
                                            ? {
                                                borderColor: "#94a3b8",
                                                color: "#64748b",
                                                "&:hover": { borderColor: "#64748b", backgroundColor: "rgba(100,116,139,0.08)" },
                                              }
                                            : {
                                                backgroundColor: "#3b82f6",
                                                color: "#fff",
                                                "&:hover": { backgroundColor: "#2563eb", boxShadow: "none" },
                                              }),
                                      }}
                                    >
                                      {label}
                                    </Button>
                                  ))}
                                </Box>
                              </Tooltip>
                            ) : (
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: "0.875rem",
                                  fontWeight: 500,
                                  color:
                                    uiStatus === "CANCELLED" || uiStatus === "FAILED"
                                      ? "#64748b"
                                      : uiStatus === "FINALIZED" || uiStatus === "APPROVED" || uiStatus === "SENT" || uiStatus === "SENDING"
                                        ? "#10b981"
                                        : uiStatus === "DELIVERY_FAILED"
                                          ? "#ef4444"
                                          : "#3b82f6",
                                }}
                              >
                                {uiStatus}
                              </Typography>
                            )}
                          </TableCell>
                        );
                      }
                      case "run_type":
                        return (
                          <TableCell key={colId} align="center" sx={sticky}>
                            {(() => {
                              const runType = (inv.generation_type || "").toUpperCase();
                              if (runType === "ON_DEMAND") {
                                return (
                                  <Tooltip title="On demand">
                                    <TouchAppIcon sx={{ color: "#3b82f6", fontSize: 22 }} />
                                  </Tooltip>
                                );
                              }
                              if (runType === "RECURRING" || runType === "SCHEDULED") {
                                return (
                                  <Tooltip title={runType === "SCHEDULED" ? "Scheduled" : "Recurring"}>
                                    <ScheduleIcon sx={{ color: "#3b82f6", fontSize: 22 }} />
                                  </Tooltip>
                                );
                              }
                              return <Typography sx={{ color: "#52525b", fontSize: "0.875rem" }}>{inv.generation_type || "—"}</Typography>;
                            })()}
                          </TableCell>
                        );
                      case "requested":
                        return (
                          <TableCell key={colId} sx={{ ...sticky, color: "#71717a" }}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                              <Typography component="span" sx={{ fontSize: "0.875rem", color: "#52525b" }}>{inv.requested_by ?? "—"}</Typography>
                              <Typography component="span" sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>{formatDateTime(inv.requested_at)}</Typography>
                            </Box>
                          </TableCell>
                        );
                      case "finalized_at":
                        return <TableCell key={colId} sx={{ ...sticky, color: "#71717a" }}>{formatDateTime(inv.finalized_at)}</TableCell>;
                      case "approved":
                        return (
                          <TableCell key={colId} sx={{ ...sticky, color: "#71717a" }}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                              <Typography component="span" sx={{ fontSize: "0.875rem", color: "#52525b" }}>{inv.approved_by ?? "—"}</Typography>
                              <Typography component="span" sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>{formatDateTime(inv.approved_at)}</Typography>
                            </Box>
                          </TableCell>
                        );
                      case "sent":
                        return (
                          <TableCell key={colId} sx={{ ...sticky, color: "#71717a" }}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                              <Typography component="span" sx={{ fontSize: "0.875rem", color: "#52525b" }}>{inv.sent_by ?? "—"}</Typography>
                              <Typography component="span" sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>{formatDateTime(inv.sent_at)}</Typography>
                            </Box>
                          </TableCell>
                        );
                      case "cancelled":
                        return (
                          <TableCell key={colId} sx={{ ...sticky, color: "#71717a" }}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                              <Typography component="span" sx={{ fontSize: "0.875rem", color: "#52525b" }}>{inv.cancelled_by ?? "—"}</Typography>
                              <Typography component="span" sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>{formatDateTime(inv.cancelled_at)}</Typography>
                              {(inv.cancel_reason ?? "").trim() && (
                                <Typography component="span" sx={{ fontSize: "0.75rem", color: "#94a3b8", fontStyle: "italic" }}>{inv.cancel_reason}</Typography>
                              )}
                            </Box>
                          </TableCell>
                        );
                      case "applied_rules":
                        return (
                          <TableCell key={colId} align="center" sx={sticky}>
                            <Tooltip title="View rule snapshots used for this invoice">
                              <Chip
                                icon={<ReceiptLongIcon sx={{ fontSize: 16 }} />}
                                label={inv.rule_application_count != null ? inv.rule_application_count : "0"}
                                size="small"
                                onClick={() => handleOpenRulesModal(inv.invoice_id)}
                                sx={{
                                  cursor: "pointer",
                                  fontWeight: 600,
                                  fontSize: "0.8125rem",
                                  borderRadius: "6px",
                                  backgroundColor: "#f1f5f9",
                                  color: "#475569",
                                  "&:hover": { backgroundColor: "#e2e8f0", color: "#334155" },
                                  "& .MuiChip-icon": { color: "inherit" },
                                }}
                              />
                            </Tooltip>
                          </TableCell>
                        );
                      case "actions":
                        return (
                          <TableCell key={colId} align="center" sx={sticky}>
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 0.25, flexWrap: "nowrap" }}>
                              <Tooltip title={String(inv?.status || inv?.ui_status || "").toUpperCase() === "GENERATING" ? "Invoice generation in progress" : "Generate invoice for this period"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={generateLoading || String(inv?.status || inv?.ui_status || "").toUpperCase() === "GENERATING"}
                                    onClick={() => handleOpenGenerateDialog(inv)}
                                    sx={{ color: "#71717a", "&:hover:not(.Mui-disabled)": { color: "#18181b", bgcolor: "rgba(0,0,0,0.04)" } }}
                                  >
                                    <PlayArrowIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={canPreviewInvoice(inv) ? "Preview invoice (opens in new tab)" : "Preview available for finalized, sent, or cancelled invoices"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!canPreviewInvoice(inv) || previewingInvoiceId === inv.invoice_id}
                                    onClick={() => handlePreviewInvoice(inv.invoice_id)}
                                    sx={{ color: "#71717a", "&:hover:not(.Mui-disabled)": { color: "#18181b", bgcolor: "rgba(0,0,0,0.04)" } }}
                                  >
                                    {previewingInvoiceId === inv.invoice_id ? (
                                      <CircularProgress size={20} sx={{ color: "#71717a" }} />
                                    ) : (
                                      <VisibilityIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={canPreviewInvoice(inv) ? "Download invoice" : "Download available for Finalized, Sent, or Cancelled invoices"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!inv.invoice_id || downloadingInvoiceId === inv.invoice_id || !canPreviewInvoice(inv)}
                                    onClick={() => handleDownloadInvoice(inv.invoice_id)}
                                    sx={{ color: "#71717a", "&:hover:not(.Mui-disabled)": { color: "#18181b", bgcolor: "rgba(0,0,0,0.04)" } }}
                                  >
                                    {downloadingInvoiceId === inv.invoice_id ? (
                                      <CircularProgress size={20} sx={{ color: "#71717a" }} />
                                    ) : (
                                      <DownloadIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        );
                      default:
                        return <TableCell key={colId} sx={sticky}>—</TableCell>;
                    }
                  };
                  return (
                    <TableRow
                      key={inv.invoice_id}
                      sx={{
                        "& td": { borderBottom: "1px solid rgba(0,0,0,0.06)", py: 1.5, px: 2, fontSize: "0.875rem" },
                        "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                      }}
                    >
                      {visibleColumns.map((col, colIndex) => renderCell(col.id, colIndex))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!isFetchingInvoices && !fetchInvoicesError && (invoicesPagination?.total ?? 0) > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
              px: 2,
              py: 1.25,
              minHeight: 48,
              borderTop: "1px solid rgba(0,0,0,0.06)",
              backgroundColor: "#fafafa",
              "& .MuiSelect-select, & .MuiInputBase-input": { py: 0.75 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography component="span" sx={{ fontSize: "0.8125rem", color: "#52525b", fontWeight: 500, lineHeight: 1 }}>
                Rows per page
              </Typography>
              <FormControl size="small" variant="outlined" sx={{ minWidth: 72 }}>
                <Select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  sx={{
                    fontSize: "0.8125rem",
                    height: 32,
                    backgroundColor: "#fff",
                    "& .MuiSelect-select": { py: "6px", lineHeight: 1.5 },
                    "& fieldset": { borderColor: "rgba(0,0,0,0.12)" },
                  }}
                >
                  {[10, 15, 25, 50].map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: "0.8125rem", color: "#52525b", fontWeight: 500, lineHeight: 1 }}>
                {(() => {
                  const total = invoicesPagination?.total ?? 0;
                  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
                  const end = Math.min(page * pageSize, total);
                  return `${start}–${end} of ${total}`;
                })()}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton
                  size="small"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                  sx={{
                    color: page <= 1 ? "#d4d4d8" : "#52525b",
                    "&:hover:not(.Mui-disabled)": { backgroundColor: "rgba(0,0,0,0.04)", color: "#18181b" },
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={page >= Math.ceil((invoicesPagination?.total ?? 0) / pageSize)}
                  onClick={() => setPage((p) => Math.min(Math.ceil((invoicesPagination?.total ?? 0) / pageSize), p + 1))}
                  aria-label="Next page"
                  sx={{
                    color: page >= Math.ceil((invoicesPagination?.total ?? 0) / pageSize) ? "#d4d4d8" : "#52525b",
                    "&:hover:not(.Mui-disabled)": { backgroundColor: "rgba(0,0,0,0.04)", color: "#18181b" },
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}
      </Card>

      {/* Status column filter popover */}
      <Popover
        open={statusFilterMenuOpen}
        anchorEl={statusFilterAnchorEl}
        onClose={() => setStatusFilterAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 200, p: 0 } }}
      >
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", px: 1.5, pt: 1.5, pb: 0.5 }}>
          Filter by status
        </Typography>
        <List dense disablePadding sx={{ pb: 0.5 }}>
          {STATUS_FILTER_MODES.map(({ value, label }) => (
            <ListItem key={value} disablePadding>
              <ListItemButton
                selected={statusFilterMode === value}
                onClick={() => handleStatusFilterModeChange(value)}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  "&.Mui-selected": { backgroundColor: "rgba(59, 130, 246, 0.08)", "&:hover": { backgroundColor: "rgba(59, 130, 246, 0.12)" } },
                }}
              >
                <ListItemText primary={label} primaryTypographyProps={{ fontSize: "0.8125rem" }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Popover>

      {/* Run type column filter popover */}
      <Popover
        open={runTypeFilterMenuOpen}
        anchorEl={runTypeFilterAnchorEl}
        onClose={() => setRunTypeFilterAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 200, p: 0 } }}
      >
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", px: 1.5, pt: 1.5, pb: 0.5 }}>
          Filter by run type
        </Typography>
        <List dense disablePadding sx={{ pb: 0.5 }}>
          {RUN_TYPE_FILTER_MODES.map(({ value, label }) => (
            <ListItem key={value} disablePadding>
              <ListItemButton
                selected={runTypeFilterMode === value}
                onClick={() => handleRunTypeFilterModeChange(value)}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  "&.Mui-selected": { backgroundColor: "rgba(59, 130, 246, 0.08)", "&:hover": { backgroundColor: "rgba(59, 130, 246, 0.12)" } },
                }}
              >
                <ListItemText primary={label} primaryTypographyProps={{ fontSize: "0.8125rem" }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Popover>

      <Dialog
        open={actionDialogOpen}
        onClose={handleCloseActionDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1rem" }}>
          {actionType ? INVOICE_ACTIONS.find((a) => a.id === actionType)?.label ?? actionType : ""}
        </DialogTitle>
        <DialogContent>
          {actionType === "CANCEL" ? (
            (() => {
              const status = (actionInvoice?.status || "").toString().toUpperCase();
              const uiStatus = (actionInvoice?.ui_status || "").toString().toUpperCase();
              const hideReason = (status === "FINALIZED" && uiStatus === "PENDING_APPROVAL") || status === "FAILED";
              return hideReason ? (
                <Typography sx={{ fontSize: "0.875rem", color: "#64748b", pt: 0.5 }}>
                  Are you sure you want to cancel this invoice?
                </Typography>
              ) : (
                <Box sx={{ pt: 0.5 }}>
                  <Typography sx={{ fontSize: "0.875rem", color: "#64748b", mb: 1.5 }}>
                    Cancel reason (optional)
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Reason for cancellation"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    multiline
                    rows={2}
                    sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
                  />
                </Box>
              );
            })()
          ) : (
            <Typography sx={{ fontSize: "0.875rem", color: "#64748b", pt: 0.5 }}>
              Are you sure you want to {actionType?.toLowerCase()} this invoice?
            </Typography>
          )}
          {actionError && (
            <Alert severity="error" sx={{ fontSize: "0.8125rem", mt: 1.5 }} onClose={() => setActionError(null)}>
              {actionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={handleCloseActionDialog} disabled={actionLoading} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={actionLoading || !!actionError}
            onClick={handleConfirmInvoiceAction}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {actionLoading ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={generateDialogOpen}
        onClose={handleCloseGenerateDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1rem" }}>
          {generateNoRules ? "No rules for this period" : "Generate invoice"}
        </DialogTitle>
        <DialogContent>
          {generateNoRules ? (
            <Box sx={{ pt: 0.5 }}>
              <Typography sx={{ color: "#64748b", fontSize: "0.875rem", mb: 1 }}>
                {generateNoRulesMessage}
              </Typography>
              <Typography sx={{ color: "#475569", fontSize: "0.8125rem", fontWeight: 500 }}>
                At least one rule needs to be applied for this period before you can generate an invoice.
              </Typography>
            </Box>
          ) : generatePeriod ? (
            <Box sx={{ pt: 0.5 }}>
              <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>
                Billing will be generated for period: <Box component="span" sx={{ fontWeight: 600, color: "#0f172a" }}>{generatePeriod.label}</Box>
              </Typography>
              {generatePoLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, mt: 2 }}>
                  <CircularProgress size={22} />
                  <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>Loading purchase orders…</Typography>
                </Box>
              ) : generatePoList.length === 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2 }}>
                  <Alert severity="info" sx={{ fontSize: "0.8125rem" }}>
                    No purchase orders yet. Create one for this customer, then open Generate invoice again.
                  </Alert>
                  <Button
                    type="button"
                    variant="contained"
                    onClick={handleGoToPurchaseOrdersFromGenerate}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    Add purchase order
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
                  <FormControl size="small" fullWidth required>
                    <InputLabel id="generate-po-label" shrink required>
                      Purchase Order
                    </InputLabel>
                    <Select
                      labelId="generate-po-label"
                      value={generatePoId}
                      label="Purchase Order"
                      onChange={(e) => setGeneratePoId(e.target.value)}
                      inputProps={{ "aria-label": "Purchase Order" }}
                      renderValue={(v) => {
                        const po = generatePoList.find((p) => p.id === v);
                        if (!po) return "—";
                        return po.order_number ? `${po.po_number ?? ""} (${po.order_number})` : (po.po_number ?? "—");
                      }}
                    >
                      {generatePoList.map((po) => (
                        <MenuItem key={po.id} value={po.id}>
                          {po.order_number ? `${po.po_number ?? "—"} (${po.order_number})` : (po.po_number ?? "—")}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    type="button"
                    variant="text"
                    size="small"
                    onClick={handleGoToPurchaseOrdersFromGenerate}
                    sx={{ textTransform: "none", fontWeight: 600, alignSelf: "flex-start", px: 0.5 }}
                  >
                    Add purchase order
                  </Button>
                </Box>
              )}
              <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mt: 1 }}>
                Do you want to continue?
              </Typography>
              {generateError && (
                <Alert severity="error" sx={{ fontSize: "0.8125rem", mt: 1.5 }} onClose={() => setGenerateError(null)}>
                  {generateError}
                </Alert>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          {generateNoRules ? (
            <Button type="button" variant="contained" onClick={handleCloseGenerateDialog} sx={{ textTransform: "none", fontWeight: 600 }}>
              Close
            </Button>
          ) : (
            <>
              <Button type="button" onClick={handleCloseGenerateDialog} disabled={generateLoading} sx={{ textTransform: "none" }}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="contained"
                disabled={
                  generateLoading ||
                  generatePoLoading ||
                  generatePoList.length === 0 ||
                  !(generatePoId != null && String(generatePoId).trim() !== "")
                }
                onClick={() => handleGenerateInvoice(false)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {generateLoading ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Generate"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={rulesModalOpen}
        onClose={handleCloseRulesModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" } }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1rem", borderBottom: "1px solid #e4e4e7", pb: 1.5 }}>
          {appliedRulesModalTitle}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 2.5, px: 2.5, minHeight: 120 }}>
          {isFetchingInvoiceRuleSnapshots ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
              <CircularProgress size={32} sx={{ color: "#3b82f6" }} />
            </Box>
          ) : fetchInvoiceRuleSnapshotsError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {typeof fetchInvoiceRuleSnapshotsError === "string"
                ? fetchInvoiceRuleSnapshotsError
                : (getApiErrorMessage(fetchInvoiceRuleSnapshotsError) || "Failed to load rule snapshots.")}
            </Alert>
          ) : invoiceRuleSnapshots?.items?.length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1.5 }}>
              {invoiceRuleSnapshots.items.map((item, idx) => {
                const snap = item.rule_snapshot || {};
                const ruleType = snap.type || snap.rule_type || "";
                const ruleCategoryDisplay = (() => {
                  if (snap.category_label != null && String(snap.category_label).trim() !== "") {
                    return snap.category_label;
                  }
                  const raw = snap.rule_category ?? snap.category;
                  if (raw == null || String(raw).trim() === "") return "—";
                  return String(raw).replace(/_/g, " ");
                })();
                const ruleSubCategoryRaw =
                  item.label != null && String(item.label).trim() !== ""
                    ? item.label
                    : snap.rule_label != null && String(snap.rule_label).trim() !== ""
                      ? snap.rule_label
                      : snap.type_label || ruleType || "—";
                const ruleSubCategoryDisplay =
                  ruleSubCategoryRaw === "—"
                    ? "—"
                    : String(ruleSubCategoryRaw).replace(
                        /(^|[^A-Za-z0-9])Margin(?=$|[^A-Za-z0-9])/gi,
                        (_, sep) => `${sep}Markup`
                      );
                const params = snap.parameters && typeof snap.parameters === "object" ? snap.parameters : {};
                const isAwsPartnerSupportSnap =
                  String(snap.type || "").toUpperCase() === AWS_PARTNER_SUPPORT_RULE_TYPE;
                const scopes = Array.isArray(snap.scopes) ? snap.scopes : [];
                const hasScopes = scopes.length > 0;
                const scopeByType = scopes.reduce((acc, s) => {
                  const t = s.scopeType || s.scope_type || "OTHER";
                  if (!acc[t]) acc[t] = [];
                  acc[t].push(s);
                  return acc;
                }, {});
                const scopeTypeLabel = (t) => {
                  if (t === "SERVICE") return "AWS Services";
                  if (t === "REGION") return "AWS Regions";
                  if (t === "LINKED_ACCOUNT") return "Linked Accounts";
                  return t.replace(/_/g, " ");
                };
                const periodStart = snap.startDate || snap.start_date;
                const periodEnd = snap.endDate || snap.end_date;
                const activePeriodText =
                  periodStart && periodEnd
                    ? `${formatDate(periodStart)} – ${formatDate(periodEnd)}`
                    : null;
                const frequencyText =
                  snap.frequency === "RECURRING"
                    ? "Recurring"
                    : snap.frequency
                      ? "One-time"
                      : null;
                const ruleMetaChipLabel = [frequencyText, activePeriodText].filter(Boolean).join(" · ");
                const ruleMetaChipColors =
                  snap.frequency === "RECURRING"
                    ? { bg: alpha("#3b82f6", 0.1), fg: "#3b82f6" }
                    : snap.frequency
                      ? { bg: alpha("#10b981", 0.1), fg: "#10b981" }
                      : { bg: alpha("#64748b", 0.12), fg: "#475569" };
                return (
                  <Accordion
                    key={item.billing_rule_id || idx}
                    disableGutters
                    square
                    sx={{
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      "&:before": { display: "none" },
                      "&.Mui-expanded": { mt: 0, mb: 0 },
                      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: "#64748b" }} />}
                      sx={{
                        backgroundColor: "#ffffff",
                        borderRadius: "8px",
                        minHeight: 56,
                        "& .MuiAccordionSummary-content": { my: 1.5, alignItems: "center" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "#0f172a", lineHeight: 1.3 }}>
                              {snap.name || "—"}
                            </Typography>
                            {ruleMetaChipLabel && (
                              <Chip
                                label={ruleMetaChipLabel}
                                size="small"
                                sx={{
                                  height: "auto",
                                  maxWidth: "100%",
                                  py: 0.25,
                                  "& .MuiChip-label": {
                                    whiteSpace: "normal",
                                    lineHeight: 1.25,
                                  },
                                  backgroundColor: ruleMetaChipColors.bg,
                                  color: ruleMetaChipColors.fg,
                                  fontWeight: 600,
                                  fontSize: "0.6875rem",
                                }}
                              />
                            )}
                          </Box>
                          {item.applied_at && (
                            <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", fontWeight: 400 }}>
                              Applied {formatDateTime(item.applied_at)}
                            </Typography>
                          )}
                        </Box>
                        {item.total_impact_amount != null && (
                          <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "#0f172a", flexShrink: 0 }}>
                            {formatCurrency(item.total_impact_amount)}
                          </Typography>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0 }}>
                      <Box
                        sx={{
                          p: 2,
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          backgroundColor: "#ffffff",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.25,
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 0.5, textTransform: "uppercase" }}>
                            Rule Category
                          </Typography>
                          <Typography sx={{ fontSize: "0.9375rem", color: "#0f172a", fontWeight: 600, lineHeight: 1.35 }}>
                            {ruleCategoryDisplay}
                          </Typography>
                        </Box>
                        <Box sx={{ borderTop: "1px solid #e2e8f0", pt: 1.25 }}>
                          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 0.5, textTransform: "uppercase" }}>
                            Sub category
                          </Typography>
                          <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 500, lineHeight: 1.4 }}>
                            {ruleSubCategoryDisplay}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Configuration Parameters */}
                      {Object.keys(params).length > 0 && (
                        <Box sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: "#ffffff" }}>
                          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 1.5, textTransform: "uppercase" }}>
                            Configuration Parameters
                          </Typography>
                          <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 1.5, lineHeight: 1.5 }}>
                            Parameters used when this rule was applied at invoice generation time.
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {Object.entries(params).map(([key, value]) => {
                              const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
                              const isAppliesToRuleTypes =
                                key === APPLIES_TO_RULE_TYPES_KEY &&
                                value != null &&
                                typeof value === "object" &&
                                !Array.isArray(value);
                              const appliesToEnabled = isAppliesToRuleTypes
                                ? getSelectedAppliesToRuleTypeValues(value)
                                : [];
                              let displayText;
                              if (isAppliesToRuleTypes) {
                                displayText = null;
                              } else if (typeof value === "boolean") {
                                displayText = value ? "Enabled" : "Disabled";
                              } else if (key === "amount") {
                                displayText = formatCurrency(value);
                              } else if (isAwsPartnerSupportSnap) {
                                displayText = formatAwsPartnerSupportParameterDisplay(key, value);
                                if (!displayText) displayText = "—";
                              } else if (value != null && (typeof value === "object" || Array.isArray(value))) {
                                try {
                                  displayText = JSON.stringify(value);
                                } catch {
                                  displayText = String(value);
                                }
                              } else {
                                displayText = String(value);
                              }
                              const showPercentSuffix =
                                key === "percent" && typeof value !== "object" && !Array.isArray(value);
                              return (
                                <Box
                                  key={key}
                                  sx={{
                                    p: 1.5,
                                    backgroundColor: "#f8fafc",
                                    borderRadius: "6px",
                                    border: "1px solid #e2e8f0",
                                    display: "flex",
                                    flexDirection: { xs: "column", sm: "row" },
                                    justifyContent: "space-between",
                                    alignItems: { xs: "flex-start", sm: isAppliesToRuleTypes ? "flex-start" : "center" },
                                    gap: 1,
                                    minWidth: 0,
                                    overflow: "hidden",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "0.875rem",
                                      color: "#1e293b",
                                      fontWeight: 600,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {label}
                                  </Typography>
                                  {isAppliesToRuleTypes ? (
                                    appliesToEnabled.length > 0 ? (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: 0.5,
                                          justifyContent: { sm: "flex-end" },
                                          minWidth: 0,
                                          maxWidth: "100%",
                                        }}
                                      >
                                        {appliesToEnabled.map((ruleType) => (
                                          <Chip
                                            key={ruleType}
                                            label={formatAppliesToRuleTypeLabel(ruleType)}
                                            size="small"
                                            sx={{
                                              fontSize: "0.75rem",
                                              backgroundColor: "#ffffff",
                                              color: "#475569",
                                              border: "1px solid #e2e8f0",
                                              fontWeight: 500,
                                            }}
                                          />
                                        ))}
                                      </Box>
                                    ) : (
                                      <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>—</Typography>
                                    )
                                  ) : (
                                    <Typography
                                      sx={{
                                        fontSize: "0.875rem",
                                        color: "#3b82f6",
                                        fontWeight: 600,
                                        minWidth: 0,
                                        maxWidth: "100%",
                                        textAlign: { sm: "right" },
                                        wordBreak: "break-word",
                                        overflowWrap: "anywhere",
                                      }}
                                    >
                                      {displayText}
                                      {showPercentSuffix ? "%" : null}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                      {/* Scope Filters */}
                      <Box sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: hasScopes ? "#ffffff" : "#f8fafc" }}>
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 1.5, textTransform: "uppercase" }}>
                          Scope Filters
                        </Typography>
                        {hasScopes ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {Object.entries(scopeByType).map(([scopeType, items]) => {
                              const hasExclude = items.some((i) => (i.operator || "").toUpperCase() === "EXCLUDE");
                              const hasInclude = items.some((i) => (i.operator || "").toUpperCase() !== "EXCLUDE");
                              const scopeHeaderSuffix = hasExclude && hasInclude ? "Exclude / Include" : hasExclude ? "Exclude" : "Include";
                              return (
                              <Box key={scopeType} sx={{ p: 1.5, backgroundColor: "#f8fafc", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                                <Typography sx={{ fontSize: "0.8125rem", color: "#1e293b", fontWeight: 600, mb: 0.75 }}>
                                  {scopeTypeLabel(scopeType)} — {scopeHeaderSuffix}
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {items.map((s, i) => (
                                    <Chip
                                      key={i}
                                      label={s.scopeValue || s.scope_value || "—"}
                                      size="small"
                                      sx={{ fontSize: "0.75rem", backgroundColor: "#ffffff", color: "#475569", border: "1px solid #e2e8f0", fontWeight: 500 }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            );})}
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>
                            No scope filters. This rule applied to all accounts, services, and regions.
                          </Typography>
                        )}
                      </Box>

                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          ) : (
            <Typography sx={{ py: 3, color: "#71717a", fontSize: "0.875rem", textAlign: "center" }}>
              No rule snapshots for this invoice.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={downloadSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setDownloadSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert onClose={() => setDownloadSnackbar((prev) => ({ ...prev, open: false }))} severity={downloadSnackbar.severity}>
          {downloadSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerInvoices;
